import { Injectable, inject, NgZone } from '@angular/core';
import { Auth, onAuthStateChanged, User } from '@angular/fire/auth';
import { 
  Firestore, 
  collection, 
  doc, 
  setDoc,
  updateDoc,
  deleteDoc,
  query, 
  where,
  orderBy,
  collectionData,
  Timestamp,
  serverTimestamp,
  getDoc,
  getDocs,
  limit as firestoreLimit,
  QueryConstraint,
  or,
  arrayUnion,
  arrayRemove
} from '@angular/fire/firestore';
import { Observable, BehaviorSubject, firstValueFrom, of, map, from, switchMap } from 'rxjs';
import { Chore, CreateChoreDto, UpdateChoreDto, ChoreCompletion } from '../interfaces/chore.interface';
import { PointsService } from './points.service';
import { ChildrenService } from './children.service';
import { FirestoreUtilsService } from './firestore-utils.service';
import { v4 as uuidv4 } from 'uuid';

@Injectable({
  providedIn: 'root'
})
export class ChoresService {
  private chores$: Observable<Chore[]> = of([]);
  private choresSubject = new BehaviorSubject<Chore[]>([]);
  private completions$: Observable<ChoreCompletion[]> = of([]);
  private completionsSubject = new BehaviorSubject<ChoreCompletion[]>([]);
  private authUnsubscribe: (() => void) | null = null;
  
  // Flag to prevent double-updates when using optimistic updates
  private skipNextFirestoreUpdate = false;

  private auth = inject(Auth);
  private firestore = inject(Firestore);
  private pointsService = inject(PointsService);
  private childrenService = inject(ChildrenService);
  private firestoreUtils = inject(FirestoreUtilsService);
  private zone = inject(NgZone);

  constructor() {
    console.log('ChoresService constructor called');
    this.initializeChores();
  }

  private initializeChores() {
    console.log('Initializing chores...');
    
    // Clean up any existing subscription
    if (this.authUnsubscribe) {
      this.authUnsubscribe();
      this.authUnsubscribe = null;
    }
    
    // Set up auth state listener
    this.authUnsubscribe = onAuthStateChanged(this.auth, 
      (user) => {
        // Run inside Angular zone to ensure proper change detection
        this.zone.run(() => this.handleAuthStateChange(user));
      },
      (error) => {
        console.error('Auth state error in ChoresService:', error);
        this.zone.run(() => {
          this.choresSubject.next([]);
          this.completionsSubject.next([]);
        });
      }
    );
  }
  
  private handleAuthStateChange(user: User | null) {
    console.log('Auth state changed in ChoresService:', user?.uid);
    
    if (!user) {
      console.log('No user logged in, clearing chores');
      this.choresSubject.next([]);
      this.completionsSubject.next([]);
      return;
    }

    try {
      console.log('User authenticated, fetching chores for:', user.uid);
      
      // Check if the user is a child or parent
      this.childrenService.getChild(user.uid).subscribe({
        next: child => {
          if (child) {
            console.log('User is a child, fetching assigned chores');
            // User is a child, only show their assigned chores
            this.fetchChoresForChild(user.uid);
          } else {
            console.log('User is a parent, fetching all chores');
            // User is a parent, show all their chores
            this.fetchChoresForParent(user.uid);
            
            // Repeatable chores are now handled by the Cloud Function
            // No need to check for them on the client side
          }
          
          // Initialize completions
          this.fetchCompletions(user.uid);
        },
        error: error => {
          console.error('Error checking if user is a child:', error);
          // Default to parent view if we can't determine
          this.fetchChoresForParent(user.uid);
          this.fetchCompletions(user.uid);
        }
      });
    } catch (error) {
      console.error('Error initializing chores:', error);
      this.choresSubject.next([]);
      this.completionsSubject.next([]);
    }
  }
  
  private async fetchChoresForChild(userId: string) {
    console.log('CHORES SERVICE - fetchChoresForChild called for userId:', userId);
    
    try {
      // Use FirestoreUtilsService to create a parent-scoped query
      // This will automatically filter by the parent's ID regardless of child mode
      const baseQuery = await this.firestoreUtils.createParentScopedQuery('chores');
      
      // Add the orderBy constraint
      const q = query(baseQuery, orderBy('createdAt', 'desc'));
      
      console.log('CHORES SERVICE - Created parent-scoped query for chores');
      
      // Get the collection data
      this.chores$ = collectionData(q, { idField: 'id' }) as Observable<Chore[]>;
    } catch (error) {
      console.error('CHORES SERVICE - Error creating parent-scoped query:', error);
      this.chores$ = of([]);
      this.choresSubject.next([]);
      return;
    }
    this.chores$.subscribe({
      next: chores => {
        console.log('Child chores fetched successfully:', chores.length);
        // We'll pass all chores to the client and let the getChoresForChild method handle filtering
        // This ensures we have all the data needed for filtering in the client
        console.log('Passing all chores to client for filtering');
        this.choresSubject.next(chores);
      },
      error: error => {
        console.error('Error fetching child chores:', error);
        this.choresSubject.next([]);
      }
    });
  }
  
  private async fetchChoresForParent(userId: string) {
    try {
      // Use FirestoreUtilsService to create a parent-scoped query
      const baseQuery = await this.firestoreUtils.createParentScopedQuery('chores');
      
      // Add the orderBy constraint
      const q = query(baseQuery, orderBy('createdAt', 'desc'));
      
      console.log('Created parent-scoped query for parent chores');
      
      this.chores$ = collectionData(q, { idField: 'id' }) as Observable<Chore[]>;
      this.chores$.subscribe({
      next: chores => {
        this.choresSubject.next(chores);
      },
      error: error => {
        console.error('Error fetching parent chores:', error);
        this.choresSubject.next([]);
      }
    });
    } catch (error) {
      console.error('Error setting up parent chores subscription:', error);
      this.choresSubject.next([]);
    }
  }
  
  private async fetchCompletions(userId: string) {
    try {
      // Use FirestoreUtilsService to create a parent-scoped query
      const baseQuery = await this.firestoreUtils.createParentScopedQuery('choreCompletions');
      
      console.log('Created parent-scoped query for completions');
      
      this.completions$ = collectionData(baseQuery, { idField: 'id' }) as Observable<ChoreCompletion[]>;
      this.completions$.subscribe({
      next: completions => {
        this.completionsSubject.next(completions);
      },
      error: error => {
        console.error('Error fetching completions:', error);
        this.completionsSubject.next([]);
      }
    });
    } catch (error) {
      console.error('Error setting up completions subscription:', error);
      this.completionsSubject.next([]);
    }
  }

  getChores(): Observable<Chore[]> {
    return this.choresSubject.asObservable();
  }

  getChore(choreId: string): Observable<Chore | null> {
    return this.choresSubject.pipe(
      map(chores => chores.find(chore => chore.id === choreId) || null)
    );
  }

  async createChore(data: CreateChoreDto): Promise<Chore> {
    const user = this.auth.currentUser;
    if (!user) throw new Error('Not authenticated');

    const choresRef = collection(this.firestore, 'chores');
    const newChoreRef = doc(choresRef);
    
    const now = new Date();
    
    // Create base chore object
    const chore: any = {
      id: newChoreRef.id,
      parentId: user.uid,
      title: data.title,
      description: data.description,
      straws: data.straws,
      category: data.category,
      difficulty: data.difficulty,
      assignedTo: data.assignedTo || '', // Single child ID instead of array
      assignToAnyone: data.assignToAnyone === true, // Explicitly set the flag
      photo: data.photo,
      createdAt: now,
      updatedAt: now
    };
    
    // Add repeatable chore fields if they exist and are not undefined
    if (data.repeatable) {
      chore.repeatable = data.repeatable;
      
      if (data.repeatFrequency) {
        chore.repeatFrequency = data.repeatFrequency;
      }
      
      // Only add day of week if it's relevant to the frequency and not undefined
      if (data.repeatDayOfWeek !== undefined && 
          (data.repeatFrequency === 'weekly' || data.repeatFrequency === 'custom')) {
        chore.repeatDayOfWeek = data.repeatDayOfWeek;
      }
      
      // Only add day of month if it's relevant to the frequency and not undefined
      if (data.repeatDayOfMonth !== undefined && 
          (data.repeatFrequency === 'monthly' || data.repeatFrequency === 'custom')) {
        chore.repeatDayOfMonth = data.repeatDayOfMonth;
      }
      
      // Add next repeat date if it exists
      if (data.nextRepeatDate) {
        chore.nextRepeatDate = data.nextRepeatDate;
      }
    }

    await setDoc(newChoreRef, chore);
    
    // If the chore is assigned to a specific child, mark that child as having unread chores
    if (chore.assignedTo) {
      await this.markChildHasUnreadChores(chore.assignedTo);
    }
    
    return chore as Chore;
  }

  async updateChore(choreId: string, data: UpdateChoreDto): Promise<void> {
    const user = this.auth.currentUser;
    if (!user) throw new Error('Not authenticated');

    const chore = await firstValueFrom(this.getChore(choreId));
    if (!chore) throw new Error('Chore not found');
    if (chore.parentId !== user.uid) throw new Error('Not authorized');

    const choreRef = doc(this.firestore, 'chores', choreId);
    
    // Create update data object with only defined values
    const updateData: any = {
      updatedAt: new Date()
    };
    
    // Add basic fields if they exist
    if (data.title !== undefined) updateData.title = data.title;
    if (data.description !== undefined) updateData.description = data.description;
    if (data.straws !== undefined) updateData.straws = data.straws;
    if (data.category !== undefined) updateData.category = data.category;
    if (data.difficulty !== undefined) updateData.difficulty = data.difficulty;
    if (data.assignedTo !== undefined) updateData.assignedTo = data.assignedTo;
    if (data.assignToAnyone !== undefined) updateData.assignToAnyone = data.assignToAnyone === true;
    if (data.photo !== undefined) updateData.photo = data.photo;
    
    // Add repeatable fields if they exist and are not undefined
    if (data.repeatable !== undefined) {
      updateData.repeatable = data.repeatable;
      
      if (data.repeatFrequency !== undefined) {
        updateData.repeatFrequency = data.repeatFrequency;
      }
      
      // Only add day of week if it's relevant to the frequency and not undefined
      if (data.repeatDayOfWeek !== undefined && 
          (data.repeatFrequency === 'weekly' || data.repeatFrequency === 'custom')) {
        updateData.repeatDayOfWeek = data.repeatDayOfWeek;
      }
      
      // Only add day of month if it's relevant to the frequency and not undefined
      if (data.repeatDayOfMonth !== undefined && 
          (data.repeatFrequency === 'monthly' || data.repeatFrequency === 'custom')) {
        updateData.repeatDayOfMonth = data.repeatDayOfMonth;
      }
      
      // Add next repeat date if it exists
      if (data.nextRepeatDate) {
        updateData.nextRepeatDate = data.nextRepeatDate;
      }
    }
    
    await updateDoc(choreRef, updateData);
  }

  async assignChore(choreId: string, childId: string): Promise<void> {
    const user = this.auth.currentUser;
    if (!user) throw new Error('Not authenticated');

    const chore = await firstValueFrom(this.getChore(choreId));
    if (!chore) throw new Error('Chore not found');
    if (chore.parentId !== user.uid) throw new Error('Not authorized');

    // With the new single-child model, we simply set the assignedTo field to the childId
    const choreRef = doc(this.firestore, 'chores', choreId);
    await updateDoc(choreRef, {
      assignedTo: childId,
      updatedAt: new Date()
    });
    
    // Mark the child as having unread chores
    await this.markChildHasUnreadChores(childId);
  }

  async unassignChore(choreId: string, childId: string): Promise<void> {
    const user = this.auth.currentUser;
    if (!user) throw new Error('Not authenticated');

    const chore = await firstValueFrom(this.getChore(choreId));
    if (!chore) throw new Error('Chore not found');
    if (chore.parentId !== user.uid) throw new Error('Not authorized');

    // With the new single-child model, we only unassign if the chore is assigned to this specific child
    if (chore.assignedTo === childId) {
      const choreRef = doc(this.firestore, 'chores', choreId);
      await updateDoc(choreRef, {
        assignedTo: '',
        updatedAt: new Date()
      });
    }
  }

  async deleteChoreCompletions(choreId: string): Promise<void> {
    const user = this.auth.currentUser;
    if (!user) throw new Error('Not authenticated');
    
    // Get all completions for this chore
    const completions = await firstValueFrom(this.getChoreCompletions(choreId));
    
    // Delete each completion
    const deletePromises = completions.map(completion => 
      deleteDoc(doc(this.firestore, `choreCompletions/${completion.id}`))
    );
    
    await Promise.all(deletePromises);
  }

  async deleteChore(choreId: string): Promise<void> {
    const user = this.auth.currentUser;
    if (!user) throw new Error('Not authenticated');

    const chore = await firstValueFrom(this.getChore(choreId));
    if (!chore) throw new Error('Chore not found');
    if (chore.parentId !== user.uid) throw new Error('Not authorized');

    // First delete all completions for this chore
    await this.deleteChoreCompletions(choreId);
    
    const choreRef = doc(this.firestore, `chores/${choreId}`);
    await deleteDoc(choreRef);
  }

  /**
   * Delete all chores for the current user regardless of completion state
   * This is a temporary method for testing purposes
   */
  async deleteAllChores(): Promise<void> {
    const user = this.auth.currentUser;
    if (!user) throw new Error('Not authenticated');

    // Get all chores for the current user
    const chores = await firstValueFrom(this.getChores());
    
    // Filter to only include chores owned by this user
    const userChores = chores.filter(chore => chore.parentId === user.uid);
    
    // Delete each chore
    const deletePromises = userChores.map(chore => this.deleteChore(chore.id));
    
    await Promise.all(deletePromises);
  }

  getChoresForChild(childId: string): Observable<Chore[]> {
    console.log('GET CHORES FOR CHILD - Starting filter for childId:', childId);
    return this.choresSubject.pipe(
      map(chores => {
        console.log('GET CHORES FOR CHILD - Total chores before filtering:', chores.length);
        
        // Count chores with assignToAnyone set to true (boolean)
        const anyoneChoresBoolean = chores.filter(c => c.assignToAnyone === true);
        // Count chores with assignToAnyone truthy (string 'true', etc.)
        const anyoneChoresTruthy = chores.filter(c => c.assignToAnyone);
        
        console.log('GET CHORES FOR CHILD - Anyone chores count:', {
          booleanTrue: anyoneChoresBoolean.length,
          truthy: anyoneChoresTruthy.length
        });
        
        // MODIFIED FILTER: Use a more lenient check for assignToAnyone
        const filteredChores = chores.filter(chore => {
          console.log('GET CHORES FOR CHILD - Evaluating chore:', {
            choreId: chore.id,
            choreTitle: chore.title,
            assignedTo: chore.assignedTo,
            assignToAnyone: chore.assignToAnyone,
            completedAt: chore.completedAt,
            completions: chore.completions?.length || 0
          });
          
          // Include chores specifically assigned to this child
          const isAssignedToChild = chore.assignedTo === childId;
          
          // Check if it's an "anyone" chore using type-safe approach
          const isAssignedToAnyone = (
            // Check if it's a boolean true
            chore.assignToAnyone === true || 
            // Or check if it's a string that represents true
            (typeof chore.assignToAnyone === 'string' && chore.assignToAnyone === 'true')
          );
          
          // Check if the chore is completed
          const isCompleted = chore.completedAt || (chore.completions && chore.completions.length > 0);
          
          console.log('GET CHORES FOR CHILD - Chore status check:', {
            choreId: chore.id,
            isAssignedToChild,
            isAssignedToAnyone,
            isCompleted
          });
          
          // For completed "anyone" chores, check if this child completed it
          if (isAssignedToAnyone && isCompleted) {
            console.log('GET CHORES FOR CHILD - Anyone chore is completed, checking completions');
            
            // Check completions to see if this child completed it
            const completions = chore.completions || [];
            console.log('GET CHORES FOR CHILD - Completions:', completions);
            
            // Look for a completion by this child
            const completedByThisChild = completions.some(c => c.childId === childId);
            
            console.log('GET CHORES FOR CHILD - Anyone chore completion check:', {
              choreId: chore.id,
              choreTitle: chore.title,
              completions: completions.length,
              completedByThisChild,
              childId
            });
            
            // CRITICAL FIX: If this child completed the chore, always show it
            if (completedByThisChild) {
              console.log('GET CHORES FOR CHILD - This child completed the anyone chore, including it');
              return true;
            }
            
            // If another child completed it, don't show it
            console.log('GET CHORES FOR CHILD - Another child completed the anyone chore, excluding it');
            return false;
          }
          
          // For non-completed chores, include if assigned to this child or to anyone
          const shouldInclude = isAssignedToChild || isAssignedToAnyone;
          
          console.log('GET CHORES FOR CHILD - Final decision for chore:', {
            choreId: chore.id,
            choreTitle: chore.title,
            shouldInclude
          });
          
          return shouldInclude;
        });
        
        console.log('GET CHORES FOR CHILD - Total chores after filtering:', filteredChores.length);
        return filteredChores;
      })
    );
  }

  getRecentCompletedChores(childId: string, limit: number = 5): Observable<{chore: Chore, completion: ChoreCompletion}[]> {
    const completionsRef = collection(this.firestore, 'choreCompletions');
    const q = query(
      completionsRef,
      where('childId', '==', childId),
      orderBy('completedAt', 'desc'),
      firestoreLimit(limit)
    );
    
    return collectionData(q, { idField: 'id' }).pipe(
      switchMap((completions) => {
        // For each completion, get the associated chore
        const chorePromises = completions.map(async completion => {
          const choreRef = doc(this.firestore, 'chores', completion['choreId'] as string);
          const choreSnap = await getDoc(choreRef);
          
          if (choreSnap.exists()) {
            const chore = { ...choreSnap.data(), id: choreSnap.id } as Chore;
            return { chore, completion };
          }
          return null;
        });
        
        // Convert Promise array to Observable
        return from(Promise.all(chorePromises)).pipe(
          map(results => results.filter(result => result !== null) as {chore: Chore, completion: ChoreCompletion}[])
        );
      })
    );
  }

  getChoreCompletions(choreId?: string, childId?: string): Observable<ChoreCompletion[]> {
    return this.completionsSubject.asObservable().pipe(
      map(completions => {
        let filtered = completions;
        if (choreId) {
          filtered = filtered.filter(c => c.choreId === choreId);
        }
        if (childId) {
          filtered = filtered.filter(c => c.childId === childId);
        }
        return filtered;
      })
    );
  }

  async markChoreComplete(choreId: string, childId: string): Promise<void> {
    const user = this.auth.currentUser;
    if (!user) throw new Error('Not authenticated');
    
    // Get the chore details
    const choreRef = doc(this.firestore, `chores/${choreId}`);
    const choreSnap = await getDoc(choreRef);
    if (!choreSnap.exists()) throw new Error('Chore not found');
    
    const chore = { ...choreSnap.data(), id: choreSnap.id } as Chore;
    
    // Check if the chore is assigned to this child or to anyone
    if (chore.assignedTo !== childId && !chore.assignToAnyone) {
      throw new Error('Chore is not assigned to this child');
    }
    
    // For "anyone" chores, check if it's already been completed
    if (chore.assignToAnyone && chore.completedAt) {
      throw new Error('This chore has already been completed by another child');
    }
    
    // Create a completion record
    const completionRef = doc(collection(this.firestore, 'choreCompletions'));
    const completion: ChoreCompletion = {
      id: completionRef.id,
      choreId,
      childId,
      parentId: chore.parentId,
      completedAt: Timestamp.now()
    };
    
    // First create the completion record
    await setDoc(completionRef, completion);
    
    // Then update the chore document with only the necessary fields
    const updateData: any = {
      completedAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    };
    
    // CRITICAL FIX: For "anyone" chores, explicitly set a property to track which child completed it
    if (chore.assignToAnyone === true || (typeof chore.assignToAnyone === 'string' && chore.assignToAnyone === 'true')) {
      updateData.completedByChildId = childId;
      console.log(`MARK CHORE COMPLETE - Set completedByChildId to ${childId} for anyone chore ${chore.title}`);
    }
    
    await updateDoc(choreRef, updateData);
    
    // Check if this is a repeatable chore and handle it
    if (chore.repeatable) {
      await this.handleRepeatableChore(chore);
    }
    
    // Finally adjust the points
    await this.childrenService.adjustPoints(childId, chore.straws, `Completed chore: ${chore.title}`);
  }

  async unmarkChoreComplete(completionId: string): Promise<void> {
    console.log('UNMARK CHORE - Starting unmark for completion:', completionId);
    const user = this.auth.currentUser;
    if (!user) throw new Error('Not authenticated');

    // Get the completion record
    const completionRef = doc(this.firestore, `choreCompletions/${completionId}`);
    const completionSnap = await getDoc(completionRef);
    if (!completionSnap.exists()) {
      console.error('UNMARK CHORE - Completion record not found:', completionId);
      throw new Error('Completion record not found');
    }
    
    const completion = completionSnap.data() as ChoreCompletion;
    console.log('UNMARK CHORE - Found completion record:', completion);
    
    if (completion.parentId !== user.uid) {
      console.error('UNMARK CHORE - Not authorized to unmark this completion');
      throw new Error('Not authorized');
    }

    // Get the chore details to adjust points
    const choreRef = doc(this.firestore, `chores/${completion.choreId}`);
    const choreSnap = await getDoc(choreRef);
    if (!choreSnap.exists()) {
      console.error('UNMARK CHORE - Chore not found:', completion.choreId);
      throw new Error('Chore not found');
    }
    
    const chore = choreSnap.data() as Chore;
    console.log('UNMARK CHORE - Found chore:', {
      choreId: completion.choreId,
      choreTitle: chore.title,
      assignedTo: chore.assignedTo,
      assignToAnyone: chore.assignToAnyone,
      completedAt: chore.completedAt
    });
    
    // Deduct the points
    console.log('UNMARK CHORE - Deducting points:', {
      childId: completion.childId,
      straws: -chore.straws
    });
    
    await this.childrenService.adjustPoints(
      completion.childId,
      -chore.straws,
      'Unmarked completed chore: ' + (chore.title || 'Untitled')
    );

    // Delete the completion record
    console.log('UNMARK CHORE - Deleting completion record:', completionId);
    await deleteDoc(completionRef);
    
    // IMPORTANT: Also update the chore document to remove the completedAt field
    console.log('UNMARK CHORE - Updating chore to remove completedAt field');
    
    // CRITICAL FIX: Also remove the completedByChildId property for "anyone" chores
    const updateData: any = {
      completedAt: null,
      updatedAt: new Date()
    };
    
    // Check if this is an "anyone" chore and remove the completedByChildId property
    if (chore.assignToAnyone === true || (typeof chore.assignToAnyone === 'string' && chore.assignToAnyone === 'true')) {
      // @ts-ignore - Remove our custom property
      if (chore.completedByChildId) {
        console.log(`UNMARK CHORE - Removing completedByChildId property for anyone chore ${chore.title}`);
        updateData.completedByChildId = null;
      }
    }
    
    await updateDoc(choreRef, updateData);
    
    console.log('UNMARK CHORE - Successfully unmarked chore as complete');
  }

  /**
   * Handles the creation of a new chore instance for repeatable chores
   * based on the repeat settings and completion status
   */
  private async handleRepeatableChore(chore: Chore): Promise<void> {
    // If the chore doesn't have repeat settings, do nothing
    if (!chore.repeatable || !chore.repeatFrequency) {
      return;
    }
    
    // No longer update lastRepeatedAt here - this should only be done by the Cloud Function
    // when it actually creates a new instance of the chore
  }
  
  /**
   * Updates the local chores array without triggering a Firestore update
   * This is used for optimistic updates to prevent UI flicker
   * @param chores The updated chores array
   */
  updateChoresLocally(chores: Chore[]): void {
    console.log('OPTIMISTIC UPDATE - Updating chores locally:', chores.length);
    
    // Log details about the chores, especially completed ones
    const completedChores = chores.filter(c => c.completedAt || (c.completions && c.completions.length > 0));
    console.log(`OPTIMISTIC UPDATE - Found ${completedChores.length} completed chores`);
    
    // Log details about anyone chores
    const anyoneChores = chores.filter(c => 
      c.assignToAnyone === true || 
      (typeof c.assignToAnyone === 'string' && c.assignToAnyone === 'true')
    );
    console.log(`OPTIMISTIC UPDATE - Found ${anyoneChores.length} 'anyone' chores`);
    
    // Log details about completed anyone chores
    const completedAnyoneChores = anyoneChores.filter(c => 
      c.completedAt || (c.completions && c.completions.length > 0)
    );
    console.log(`OPTIMISTIC UPDATE - Found ${completedAnyoneChores.length} completed 'anyone' chores`);
    
    // Log details of each completed anyone chore
    completedAnyoneChores.forEach(chore => {
      console.log('OPTIMISTIC UPDATE - Completed anyone chore details:', {
        id: chore.id,
        title: chore.title,
        completedAt: chore.completedAt ? 'Yes' : 'No',
        // @ts-ignore - Log our custom property
        completedByChildId: chore.completedByChildId || 'Not set',
        completions: chore.completions ? chore.completions.map(c => ({ id: c.id, childId: c.childId })) : 'None'
      });
    });
    
    this.skipNextFirestoreUpdate = true;
    this.choresSubject.next(chores);
    console.log('OPTIMISTIC UPDATE - BehaviorSubject updated with new chores');
  }

  /**
   * Marks a child as having unread chores
   * @param childId ID of the child to mark
   */
  private async markChildHasUnreadChores(childId: string): Promise<void> {
    try {
      // First check if the child document exists and has the unreadChores field
      const childRef = doc(this.firestore, 'children', childId);
      const childSnap = await getDoc(childRef);
      
      if (!childSnap.exists()) {
        console.error(`Child ${childId} not found, cannot mark unread chores`);
        return;
      }
      
      const childData = childSnap.data();
      const now = new Date();
      
      // Update the child document with the unreadChores field
      // If the field doesn't exist yet, this will create it
      await updateDoc(childRef, {
        unreadChores: true,
        lastChoreViewTime: childData['lastChoreViewTime'] || now,
        updatedAt: now
      });
      
      console.log(`Marked child ${childId} as having unread chores`);
    } catch (error) {
      console.error('Error marking child as having unread chores:', error);
    }
  }
}